import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
    Box,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';

export interface Column<T> {
    key: string;
    label: string;
    filterable?: boolean;
    render?: (value: unknown, row: T) => React.ReactNode;
}

type DataTableProps<T> = {
    columns: Column<T>[];
    data: T[];
    getRowId: (row: T) => string;
    onApprove?: (row: T) => void;
    onEdit?: (row: T) => void;
    onDelete?: (row: T) => void;
};

const cellValue = (row: unknown, key: string): unknown =>
    (row as Record<string, unknown>)[key];

export function DataTable<T>({
    columns,
    data,
    getRowId,
    onApprove,
    onEdit,
    onDelete,
}: DataTableProps<T>) {
    const [sortCol, setSortCol] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [filters, setFilters] = useState<Record<string, string>>({});

    const rows = useMemo(() => {
        let result = [...data];
        Object.entries(filters).forEach(([col, value]) => {
            if (!value) return;
            const needle = value.toLowerCase();
            result = result.filter((row) =>
                String(cellValue(row, col) ?? '')
                    .toLowerCase()
                    .includes(needle)
            );
        });
        if (sortCol) {
            result.sort((a, b) => {
                const av = cellValue(a, sortCol) ?? '';
                const bv = cellValue(b, sortCol) ?? '';
                const cmp =
                    typeof av === 'number' && typeof bv === 'number'
                        ? av - bv
                        : String(av).localeCompare(String(bv));
                return sortDir === 'asc' ? cmp : -cmp;
            });
        }
        return result;
    }, [data, filters, sortCol, sortDir]);

    const toggleSort = (col: string) => {
        if (sortCol === col) {
            setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortCol(col);
            setSortDir('asc');
        }
    };

    const hasActions = Boolean(onApprove || onEdit || onDelete);

    return (
        <Box>
            <TableContainer sx={{ backgroundColor: 'background.paper' }}>
                <Table size="small" stickyHeader sx={{ backgroundColor: 'background.paper' }}>
                    <TableHead sx={{ backgroundColor: 'background.paper' }}>
                        <TableRow sx={{ backgroundColor: 'background.paper' }}>
                            {columns.map((c) => (
                                <TableCell key={c.key} sx={{ backgroundColor: 'background.paper' }}>
                                    <TableSortLabel
                                        active={sortCol === c.key}
                                        direction={
                                            sortCol === c.key ? sortDir : 'asc'
                                        }
                                        onClick={() => toggleSort(c.key)}
                                    >
                                        {c.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            {hasActions && <TableCell sx={{ backgroundColor: 'background.paper' }}>Actions</TableCell>}
                        </TableRow>
                        <TableRow sx={{ backgroundColor: 'background.paper' }}>
                            {columns.map((c) => (
                                <TableCell key={c.key} sx={{ py: 0.5, backgroundColor: 'background.paper' }}>
                                    {c.filterable !== false && (
                                        <TextField
                                            placeholder="filter…"
                                            size="small"
                                            variant="standard"
                                            fullWidth
                                            value={filters[c.key] || ''}
                                            onChange={(e) =>
                                                setFilters((f) => ({
                                                    ...f,
                                                    [c.key]: e.target.value,
                                                }))
                                            }
                                            inputProps={{
                                                'aria-label': `Filter ${c.label}`,
                                                style: { fontSize: 12 },
                                            }}
                                        />
                                    )}
                                </TableCell>
                            ))}
                            {hasActions && <TableCell sx={{ backgroundColor: 'background.paper' }} />}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{ backgroundColor: 'background.paper' }}>
                        {rows.length === 0 && (
                            <TableRow sx={{ backgroundColor: 'background.paper' }}>
                                <TableCell
                                    colSpan={
                                        columns.length + (hasActions ? 1 : 0)
                                    }
                                    align="center"
                                    sx={{ color: 'text.secondary', py: 3 }}
                                >
                                    No rows
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((row) => (
                            <TableRow key={getRowId(row)} hover sx={{ backgroundColor: 'background.paper' }}>
                                {columns.map((c) => {
                                    const value = cellValue(row, c.key);
                                    return (
                                        <TableCell
                                            key={c.key}
                                            title={
                                                typeof value === 'string'
                                                    ? value
                                                    : undefined
                                            }
                                            sx={{
                                                backgroundColor: 'background.paper',
                                                maxWidth: 260,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {c.render
                                                ? c.render(value, row)
                                                : String(value ?? '—')}
                                        </TableCell>
                                    );
                                })}
                                {hasActions && (
                                    <TableCell sx={{ backgroundColor: 'background.paper', whiteSpace: 'nowrap' }}>
                                        {onApprove && (
                                            <Tooltip title="Approve">
                                                <IconButton
                                                    size="small"
                                                    color="success"
                                                    onClick={() =>
                                                        onApprove(row)
                                                    }
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {onEdit && (
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    color="info"
                                                    onClick={() => onEdit(row)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {onDelete && (
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() =>
                                                        onDelete(row)
                                                    }
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', px: 1.5, py: 0.75 }}
            >
                {rows.length} / {data.length} rows
            </Typography>
        </Box>
    );
}
